import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import {useDefaultAPI} from "@/composables/api";
import {computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch} from "vue";
import GalleryGrid from "@/components/gallery/GalleryGrid.vue";
import {authFailure} from "@/composables/api";
import GalleryFooter from "@/components/gallery/GalleryFooter.vue";
import InfoBar from "@/components/gallery/InfoBar.vue";
import GalleryHeader from "@/components/gallery/GalleryHeader.vue";
import PrintDialog from "@/components/gallery/PrintDialog.vue";
import PasswordDialog from "@/components/gallery/PasswordDialog.vue";
import {useImageCollage} from "@/composables/imageCollage";
import {galleryImages as imgs} from "@/stores/gallery";
import {
  CancelFunction,
  ScrollAction,
  ScrollDirection,
  ScrollEvent,
  useAutomaticPageScroll,
  useScrollEvents
} from "@/composables/pageScroll";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const scrollEvents = useScrollEvents()
const collage = useImageCollage()
const api = useDefaultAPI()

const keyStorageSelectionMode = "boothy:selectionMode";
const keyStorageAutomaticPageScroll = "boothy:automaticPageScroll"

const showAdminMenu = ref<boolean>(false)

const infoMessage = ref<string>("")

let automaticPageScroll: (a:ScrollAction) => CancelFunction

const latestPollTimestamp = ref<number>(-1)

const earliestImageTimestamp = computed(() : undefined | number => {
  if (imgs.value.length <= 0) {
    return undefined
  }
  return imgs.value[imgs.value.length-1].timestamp-1
})

const earliestImagePresent = computed(() => {
  if (imgs.value.length <= 0) {
    return false
  }
  return imgs.value[imgs.value.length-1].isEarliest
})

const imageSelection = ref<number[]>([])

const selectionMode = ref<boolean>(localStorage.getItem(keyStorageSelectionMode) === 'true')
const printPreviewURI = ref<string>("")

const printPreviewVisible = ref<boolean>(false)
const fetchingEarlierImages = ref<boolean>(false)

const automaticPageScrollActive = ref<boolean>(localStorage.getItem(keyStorageAutomaticPageScroll) === 'true')

let cancelPageScroll:CancelFunction
const pollLatestImages = () => {
  let opts = undefined
  if (latestPollTimestamp.value) {
    opts = {since: latestPollTimestamp.value}
  }
  api.getImages(opts).then((res) => {
    if (res && res.length > 0) {
      imgs.value = res.concat(imgs.value)
    }
    latestPollTimestamp.value = new Date().getTime()
  })
}

const fetchEarlierImages = (cb:(change: boolean) => void) => {
  if (imgs.value.length <= 0 || earliestImagePresent.value || fetchingEarlierImages.value) {
    cb(false)
    return
  }
  let opts = undefined
  if (earliestImageTimestamp.value) {
    opts = {before: earliestImageTimestamp.value}
  }
  fetchingEarlierImages.value = true
  api.getImages(opts).then((res)=>{
    if (res && res.length > 0) {
      imgs.value = imgs.value.concat(res)
      cb(true)
      return
    }
    cb(false)
  }).finally(() => {
    fetchingEarlierImages.value = false
  })
}

const removeFromSelection = (id: number) => {
  imageSelection.value = imageSelection.value.filter(el => el !== id)
}

const addToSelection = (id: number) => {
  imageSelection.value.push(id)
}

const updateSelectionMode = (val:boolean) => {
  localStorage.setItem(keyStorageSelectionMode, `${val}`)
  selectionMode.value = val
}

const toggleAutomaticPageScroll = () => {
  setAutomaticPageScroll(!automaticPageScrollActive.value)
}

const setAutomaticPageScroll = (val:boolean) => {
  automaticPageScrollActive.value = val
  localStorage.setItem(keyStorageAutomaticPageScroll, `${automaticPageScrollActive.value}`)
}

const displayInfoTooManySelections = () => {
  console.log("test trigger")
  infoMessage.value = "Es können maximal drei Bilder ausgewählt werden."
}

const invokePrint = () => {
  const selection = imgs.value.filter(i => imageSelection.value.findIndex(ts => ts === i.timestamp) !== -1)
  if (selection.length <= 0) {
    infoMessage.value = "Bitte mindestens ein Bild auswählen."
    return
  }
  collage.create(selection).then(({uri}) => {
    printPreviewURI.value = uri
    printPreviewVisible.value = true
  })
}

const userCancel = (e:KeyboardEvent) => {
  if (e.code === 'KeyS' || e.code === 'Escape') {
    setAutomaticPageScroll(false)
  }
}

const invokePageScroll = (active: boolean) => {
  if (active) {
    cancelPageScroll = automaticPageScroll(ScrollAction.ScrollBottom)
    document.addEventListener('keydown', userCancel)
  } else {
    if (cancelPageScroll) {
      cancelPageScroll()
    }
    document.removeEventListener('keydown', userCancel)
  }
}

watch(() => automaticPageScrollActive.value, invokePageScroll)

const invokeDeletion = () => {
  api.deleteImages(imageSelection.value).then(() => {
    // remove deleted images from array
    imgs.value = imgs.value.filter(i => imageSelection.value.findIndex(ts => ts === i.timestamp) === -1)
    imageSelection.value = []
  })
}

const poller = setInterval(() => {
  if (!automaticPageScrollActive.value) {
    pollLatestImages()
  }
}, 5000)

onBeforeUnmount(() => {
  clearInterval(poller)
})

pollLatestImages()

scrollEvents((e:ScrollEvent) => {
  // don't react if automatic page scroll is active
  if (automaticPageScrollActive.value) {
    return
  }
  if (e === ScrollEvent.ReachedBottom) {
    fetchEarlierImages(()=>{return})
  }
})


onMounted(() => {
  automaticPageScroll = useAutomaticPageScroll((d) => {
    return new Promise<ScrollAction>((resolve) => {
      if (d === ScrollDirection.Down) {
        fetchEarlierImages((change) => {
          if (change) {
            setTimeout(() => resolve(ScrollAction.ScrollBottom), 3000)
            return
          }
          window.location.href = "/gallery"
        })
      } else {
        resolve(ScrollAction.ScrollBottom)
      }
    })
  })
  // wait with scroll invocation of the page if it was automatically scrolling previously for about 5 seconds
  setTimeout(() => invokePageScroll(automaticPageScrollActive.value), 5000)
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(PasswordDialog, { show: _unref(authFailure) }, null, 8, ["show"]),
    _createVNode(PrintDialog, {
      uri: printPreviewURI.value,
      show: printPreviewVisible.value,
      onClosed: _cache[0] || (_cache[0] = ($event: any) => {printPreviewURI.value = ''; printPreviewVisible.value = false}),
      onCleared: _cache[1] || (_cache[1] = ($event: any) => (imageSelection.value=[]))
    }, null, 8, ["uri", "show"]),
    _createVNode(GalleryHeader, {
      "selection-mode": selectionMode.value,
      "selection-amount": imageSelection.value.length,
      onAdminMenuToggled: _cache[2] || (_cache[2] = ($event: any) => (showAdminMenu.value = !showAdminMenu.value)),
      onPrintTriggered: invokePrint,
      onDiscardSelectionTriggered: _cache[3] || (_cache[3] = ($event: any) => (imageSelection.value=[]))
    }, null, 8, ["selection-mode", "selection-amount"]),
    _createVNode(InfoBar, {
      message: infoMessage.value,
      onHidden: _cache[4] || (_cache[4] = ($event: any) => (infoMessage.value = ''))
    }, null, 8, ["message"]),
    _createVNode(GalleryGrid, {
      "selection-mode": selectionMode.value,
      selection: imageSelection.value,
      "show-bottom-spinner": fetchingEarlierImages.value,
      onImageSelected: addToSelection,
      onImageUnselected: removeFromSelection,
      onTooManyImagesSelected: displayInfoTooManySelections
    }, null, 8, ["selection-mode", "selection", "show-bottom-spinner"]),
    _createVNode(GalleryFooter, {
      show: showAdminMenu.value,
      onSelectionModeToggled: updateSelectionMode,
      onScrollModeToggled: toggleAutomaticPageScroll,
      onDeletionTriggered: invokeDeletion
    }, null, 8, ["show"])
  ], 64))
}
}

})